import {
  Component,
  HostListener,
  forwardRef,
  ElementRef,
  ViewChild,
  signal,
  computed,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-icons-dropdown',
  templateUrl: './icons-dropdown.component.html',
  styleUrls: ['./icons-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IconsDropdownComponent),
      multi: true,
    },
  ],
})
export class IconsDropdownComponent implements ControlValueAccessor {
  @ViewChild('dropdownButton') dropdownButton!: ElementRef;
  @ViewChild('dropdownPanel') dropdownPanel!: ElementRef;

  searchTerm = signal('');
  selectedIcon: string = 'home';
  isOpen = false;
  onChange: (icon: string) => void = () => {};
  onTouched: () => void = () => {};

  /** All icons from LucideIconsModule, grouped by category (name only). */
  icons: { name: string }[] = [
    // Navigation / home
    { name: 'home' },
    { name: 'menu' },
    { name: 'layout-dashboard' },
    { name: 'layout-grid' },
    { name: 'panels-top-left' },
    { name: 'panel-top-open' },
    { name: 'panel-top-close' },
    { name: 'rectangle-ellipsis' },
    { name: 'compass' },
    { name: 'navigation' },
    // Documents / files / folders
    { name: 'file-text' },
    { name: 'file-clock' },
    { name: 'file-spreadsheet' },
    { name: 'file-check' },
    { name: 'file-x' },
    { name: 'file-search' },
    { name: 'file-box' },
    { name: 'file-question' },
    { name: 'book-text' },
    { name: 'book-dashed' },
    { name: 'book-marked' },
    { name: 'library' },
    { name: 'folder' },
    { name: 'folder-open' },
    { name: 'folder-cog' },
    { name: 'folder-kanban' },
    { name: 'notebook-tabs' },
    { name: 'inbox' },
    { name: 'square-kanban' },
    { name: 'folder-plus' },
    { name: 'file-plus' },
    { name: 'clipboard-list' },
    { name: 'clipboard-check' },
    { name: 'archive' },
    { name: 'package' },
    // Charts / data
    { name: 'bar-chart-3' },
    { name: 'pie-chart' },
    { name: 'line-chart' },
    { name: 'area-chart' },
    { name: 'trending-up' },
    { name: 'activity' },
    { name: 'circle-gauge' },
    { name: 'gauge' },
    { name: 'target' },
    { name: 'sliders-horizontal' },
    // Finance / business
    { name: 'hand-coins' },
    { name: 'circle-dollar-sign' },
    { name: 'landmark' },
    { name: 'building-2' },
    { name: 'briefcase' },
    { name: 'wallet' },
    { name: 'credit-card' },
    { name: 'banknote' },
    { name: 'scale' },
    // Calendar / time
    { name: 'calendar' },
    { name: 'calendar-clock' },
    { name: 'calendar-cog' },
    { name: 'clock' },
    { name: 'timer' },
    { name: 'history' },
    // People / user
    { name: 'user' },
    { name: 'user-check' },
    { name: 'user-plus' },
    { name: 'user-cog' },
    { name: 'users' },
    { name: 'eye' },
    // Tools / actions
    { name: 'search' },
    { name: 'filter' },
    { name: 'settings' },
    { name: 'wrench' },
    { name: 'hammer' },
    { name: 'plus' },
    { name: 'edit' },
    { name: 'edit-3' },
    { name: 'pencil' },
    { name: 'trash-2' },
    { name: 'undo' },
    { name: 'undo-2' },
    { name: 'rotate-ccw' },
    { name: 'refresh-cw' },
    { name: 'save' },
    { name: 'replace' },
    { name: 'x' },
    { name: 'check' },
    { name: 'loader' },
    { name: 'loader-2' },
    { name: 'fullscreen' },
    { name: 'maximize-2' },
    { name: 'minimize-2' },
    { name: 'fold-vertical' },
    { name: 'unfold-vertical' },
    { name: 'minus-square' },
    { name: 'plus-square' },
    { name: 'zoom-in' },
    { name: 'zoom-out' },
    { name: 'list-plus' },
    { name: 'list' },
    { name: 'list-x' },
    { name: 'combine' },
    { name: 'git-pull-request-draft' },
    { name: 'git-pull-request-create-arrow' },
    // Communication / media
    { name: 'bell' },
    { name: 'info' },
    { name: 'help-circle' },
    { name: 'newspaper' },
    { name: 'megaphone' },
    { name: 'sticky-note' },
    { name: 'external-link' },
    { name: 'link' },
    { name: 'message-circle-question' },
    { name: 'mail' },
    { name: 'send' },
    { name: 'message-square' },
    { name: 'message-circle' },
    // Alerts / status
    { name: 'alert-triangle' },
    { name: 'alert-circle' },
    { name: 'check-circle' },
    { name: 'check-circle-2' },
    { name: 'x-circle' },
    { name: 'circle-x' },
    { name: 'circle' },
    { name: 'shield' },
    { name: 'lock' },
    { name: 'lock-open' },
    // Maps / location
    { name: 'map' },
    { name: 'pin' },
    { name: 'map-pin' },
    { name: 'map-pin-plus' },
    { name: 'map-pin-house' },
    { name: 'map-pinned' },
    // Media / resources
    { name: 'camera' },
    { name: 'video' },
    { name: 'headphones' },
    { name: 'mic' },
    { name: 'image' },
    // Education / reference
    { name: 'book-open' },
    { name: 'graduation-cap' },
    // Favorites / highlights
    { name: 'heart' },
    { name: 'bookmark' },
    { name: 'sparkles' },
    { name: 'zap' },
    // Reports / tables
    { name: 'layers' },
    { name: 'table' },
    { name: 'presentation' },
    // Misc / dev / other
    { name: 'star' },
    { name: 'flag' },
    { name: 'bug' },
    { name: 'award' },
    { name: 'gavel' },
    { name: 'eraser' },
    { name: 'database-backup' },
    { name: 'database' },
    { name: 'boxes' },
    { name: 'server-crash' },
    { name: 'log-out' },
  ];

  filteredIcons = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    if (!term) return this.icons;
    return this.icons.filter((icon) => icon.name.toLowerCase().includes(term));
  });

  @HostListener('document:click', ['$event.target'])
  public onClick(target: EventTarget | null): void {
    const el = target instanceof HTMLElement ? target : null;
    if (!el) return;
    const clickedInside =
      el.closest('#dropdown-button') || el.closest('.dropdown-panel');
    if (!clickedInside) {
      this.isOpen = false;
    }
  }

  writeValue(obj: unknown): void {
    this.selectedIcon = (obj as string) || 'home';
  }

  registerOnChange(fn: (icon: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchTerm.set('');
      setTimeout(() => this.updateDropdownPosition());
    }
  }

  private updateDropdownPosition(): void {
    if (!this.dropdownButton?.nativeElement || !this.dropdownPanel?.nativeElement)
      return;
    const button = this.dropdownButton.nativeElement as HTMLElement;
    const panel = this.dropdownPanel.nativeElement as HTMLElement;
    const buttonRect = button.getBoundingClientRect();
    panel.style.position = 'fixed';
    panel.style.top = `${buttonRect.bottom + window.scrollY}px`;
    panel.style.left = `${buttonRect.left + window.scrollX}px`;
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onScrollOrResize(): void {
    if (this.isOpen) this.updateDropdownPosition();
  }

  onSearchInput(value: string): void {
    this.searchTerm.set(value);
  }

  selectIcon(icon: string): void {
    this.selectedIcon = icon;
    this.onChange(icon);
    this.isOpen = false;
  }
}
